<template>
  <div
    class="rounded-full z-10 p-2 bg-slate-middle h-9 w-9 flex items-center justify-center cursor-pointer mr-2"
    tabindex="0"
    @pointerup="displayNotifications()"
    @keydown.enter="displayNotifications()"
  >
    <img :src="NotificationIcon" class="w-3.5 invert" :alt="t('aria-notifications')" />
    <span v-if="state.hasUnreadNotifications" class="absolute ml-6 mb-6 flex h-3 w-3">
      <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
      <span class="relative rounded-full h-3 w-3 bg-sky-500 text-md flex justify-center items-center"></span>
    </span>
    <div
      v-if="state.notificationListIsVisible"
      class="fadein absolute overflow-scroll overflow-x-hidden lg:w-1/5 md:w-1/3 w-3/4 max-h-96 rounded-2xl right-0 text-slate-white rounded-bl-lg top-14"
    >
      <div v-for="(notification, index) in state.notifications" :key="index" class="p-1.5 m-1.5 rounded-md flex items-center bg-slate-bright">
        <div class="flex flex-col justify-start items-start p-2">
          <p class="text-md text-left">{{ notification.message }}</p>
          <div class="mt-1 text-sm text-slate-bright w-full flex items-end justify-between">
            <div class="rounded w-1 p-1" :style="'background-color: ' + colour(notification.title).background"></div>
            <p>{{ common.actions.getCleanTimestamp(notification.timestamp) }}</p>
          </div>
        </div>
      </div>
      <div v-if="state.notifications.length === 0" class="p-1.5 m-1.5 rounded-md flex justify-center items-center" :class="notificationStyles">
        <p class="text-md p-6">{{ t('emptynotifications') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { reactive, watch, onBeforeMount, computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { ScreensizeType } from '@/constants'
  import { DialogMessage } from '@/common/classes'

  import useCommonStore from '@/composables/common'
  import useDialogStore from '@/composables/dialog'
  import useColorStore from '@/composables/colors'

  import NotificationIcon from '@/assets/icons/fontawesome/bell-regular.svg'

  interface State {
    notificationListIsVisible: boolean
    hasUnreadNotifications: boolean
    notifications: DialogMessage[]
  }

  const common = useCommonStore()
  const color = useColorStore()
  const dialogStore = useDialogStore()

  const messages = {
    no: {
      emptynotifications: 'Du har ingen nye meldinger',
    },
    en: {
      emptynotifications: "You don't have any new notifications",
    },
  }

  const { t } = useI18n({ messages })

  const state: State = reactive({
    notificationListIsVisible: false,
    hasUnreadNotifications: false,
    notifications: [],
  })

  onBeforeMount(() => {
    state.notifications = dialogStore.state.notifications
    sortNotifications()
  })

  watch(dialogStore.state, (storeState) => {
    if (dialogStore.getters.hasNewNotifications()) {
      state.notifications = storeState.notifications
      state.hasUnreadNotifications = true
      sortNotifications()
    }
  })

  const notificationStyles = computed(() => {
    return common.getters.screensizeType.value === ScreensizeType.phone ? 'bg-slate-black' : 'bg-slate-middle'
  })

  function sortNotifications() {
    state.notifications.sort((c, p) => {
      return c.timestamp > p.timestamp ? -1 : 1
    })
  }

  function displayNotifications() {
    state.notificationListIsVisible = !state.notificationListIsVisible
    if (state.notificationListIsVisible) {
      state.hasUnreadNotifications = false
    }
  }

  function colour(name: string) {
    return color.actions.selectColor(name)
  }
</script>

<style scoped></style>
